import React from "react";
import { Button, ColorPicker, ConfigProvider } from 'antd';
import { BgColorsOutlined, CloseOutlined } from '@ant-design/icons';

function ThemeCustomizer({ primaryColor, setPrimaryColor, isSettingVisible, setIsSettingVisible }) {
  // 颜色选择器的预设颜色
  const presets = [
    {
      label: '推荐',
      colors: [
        '#791A3E',  // 贯石红（品牌色）
        '#1D6FF0',  // 沉稳蓝
        '#27A37E',  // 优雅绿
        '#2A3F54',  // 商务蓝灰 - 企业后台常用色
        '#FF6B6B',  // 暖红色
        '#334155',  // 深空灰
      ],
    },
  ];

  return (
    <>
      {/* 移动端和平板的居中模态框 */}
      <div className={`lg:hidden fixed inset-0 bg-black/50 transition-opacity duration-300 z-[9999] ${
        isSettingVisible ? 'opacity-100 visible' : 'opacity-0 invisible'
      }`}>
        <div className="flex items-center justify-center min-h-screen p-4">
          <div 
            className={`bg-white shadow-lg p-6 rounded-lg w-full max-w-[280px] transition-all duration-300 transform ${
              isSettingVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
            }`}
          >
            <div className="flex flex-col gap-5">
              {/* 标题栏 */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-1 h-4 rounded-full"
                    style={{ backgroundColor: primaryColor }}
                  />
                  <span className="text-gray-800 font-medium text-base">主题定制</span>
                </div>
                {/* 关闭按钮改为叉叉图标 */}
                <Button 
                  type="text"
                  icon={<CloseOutlined className="text-gray-500 hover:text-gray-700" />}
                  onClick={() => setIsSettingVisible(false)}
                />
              </div>
              
              {/* 颜色选择器部分保持不变 */}
              <div className="space-y-3">
                <div className="text-gray-500 text-sm">主题色</div>
                <ColorPicker
                  presets={presets}
                  value={primaryColor}
                  onChange={(color) => setPrimaryColor(color.toHexString())}
                  className="w-full"
                  format="hex"
                />
              </div>

              <div className="space-y-3">
                <div className="text-gray-500 text-sm">预设主题</div>
                <div className="flex flex-wrap gap-2">
                  {presets[0].colors.map((color, index) => (
                    <div
                      key={index}
                      className="w-8 h-8 rounded-full cursor-pointer border-2 transition-all duration-200 hover:scale-110 hover:shadow-md"
                      style={{ 
                        backgroundColor: color,
                        borderColor: primaryColor === color ? color : 'transparent',
                        boxShadow: primaryColor === color ? `0 0 0 2px white, 0 0 0 4px ${color}` : 'none'
                      }}
                      onClick={() => setPrimaryColor(color)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 大屏幕的右侧抽屉 */}
      <div className="hidden lg:flex fixed right-8 bottom-16 items-end z-10 pointer-events-none">
        <div 
          className={`bg-white shadow-lg p-6 rounded-lg transition-all duration-300 transform ${
            isSettingVisible ? 'translate-x-0 opacity-100 visible' : 'translate-x-full opacity-0 invisible pointer-events-auto'
          }`}
          style={{ width: '280px', marginRight: '-1px' }}
        >
          <div className="flex flex-col gap-5 pointer-events-auto">
            {/* 标题栏 */}
            <div className="flex items-center gap-2">
              <div 
                className="w-1 h-4 rounded-full"
                style={{ backgroundColor: primaryColor }}
              />
              <span className="text-gray-800 font-medium text-base">主题定制</span>
            </div>
            
            {/* 颜色选择器 */}
            <div className="space-y-3">
              <div className="text-gray-500 text-sm">主题色</div>
              <ColorPicker
                presets={presets}
                value={primaryColor}
                onChange={(color) => setPrimaryColor(color.toHexString())}
                className="w-full"
                format="hex"
              />
            </div>

            {/* 预设主题色 */}
            <div className="space-y-3">
              <div className="text-gray-500 text-sm">预设主题</div>
              <div className="flex flex-wrap gap-2">
                {presets[0].colors.map((color, index) => (
                  <div
                    key={index}
                    className="w-8 h-8 rounded-full cursor-pointer border-2 transition-all duration-200 hover:scale-110 hover:shadow-md"
                    style={{ 
                      backgroundColor: color,
                      borderColor: primaryColor === color ? color : 'transparent',
                      boxShadow: primaryColor === color ? `0 0 0 2px white, 0 0 0 4px ${color}` : 'none'
                    }}
                    onClick={() => setPrimaryColor(color)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* 切换按钮 */}
      </div>
      <Button 
          icon={<BgColorsOutlined className={`transition-transform duration-300 ${
            isSettingVisible ? 'scale-x-[-1]' : ''
          }`} />}
          className="flex items-center justify-center w-10 h-10 rounded-tl-full rounded-bl-full bg-white shadow-lg hover:bg-slate-200 active:bg-white !bg-white fixed right-0 bottom-16 z-10"
          type="text"
          onClick={() => setIsSettingVisible(!isSettingVisible)}
        />
      {/* 小屏幕的切换按钮 */}
      <div className="lg:hidden fixed right-0 bottom-8 flex items-end z-[9999]">
        <Button 
          icon={<BgColorsOutlined className={`transition-transform duration-300 ${
            isSettingVisible ? 'scale-x-[-1]' : ''
          }`} />}
          className="flex items-center justify-center w-10 h-10 rounded-tl-full rounded-bl-full bg-white shadow-lg hover:bg-slate-200 active:bg-white !bg-white"
          type="text"
          onClick={() => setIsSettingVisible(!isSettingVisible)}
        />
      </div>
    </>
  );
}

export default ThemeCustomizer; 